/**
 * Geometry engine for user-created floor plans.
 *
 * Converts the editor's parametric "chained perimeter walk" (wall lengths +
 * turn angles) into the exact wire shape FloorPlanViewer renders: 16-float
 * column-major 4x4 transforms, [w, h, d] meter dimensions, and local-plane
 * polygonCorners. Pure module — no React, no Three.js — so it unit-tests
 * without a WebGL mock and could move to a worker untouched.
 *
 * Conventions (matching RoomPlan captured scans):
 *  - World space: XZ ground plane, +Y up, meters.
 *  - Headings are degrees; 0° points along +X, positive turns are
 *    clockwise viewed from above (a rectangle of right turns closes).
 *  - Wall local frame: +X along the wall, +Y up, polygon is a rectangle
 *    centered on the origin (same convention as `inferRectanglePolygon`).
 *  - Floor local frame: +X → world X, +Y → world -Z, so the floor polygon
 *    lies in the local X-Y plane the viewer extrudes.
 */

import { randomId } from './randomId';
import { HEADING_COLOR, RESIZE_PIN_COLOR } from './sceneUtils';

// Endpoints closer than this (meters) count as a closed loop — covers
// floating-point drift from chained trig without hiding real gaps.
export const CLOSURE_TOLERANCE_M = 0.01;

export const DEFAULT_WALL_HEIGHT_M = 2.44;

// A saved wall whose dimensions[1] is within this of its section's
// wall_height_m parses as "no override" (heightM absent), so later section
// height edits keep propagating to it. 5mm sits below the panel's 2-decimal
// display rounding but far above float noise — and real overrides (pony
// walls, slope ends) differ by tens of centimeters.
export const HEIGHT_OVERRIDE_EPSILON_M = 0.005;

const toRadians = (deg) => (deg * Math.PI) / 180;

// Normalize a turn angle into [-180, 180) using a TRUE modulo (JS `%` is
// signed, so a naive `(t + 540) % 360` leaves large negative inputs out of
// range). Guarantees a stored/sent turn is within the server's ±360 bound.
// Shared by the panel's Close Loop and the anchor-heading presets so both
// computed-heading paths normalize identically.
export const normalizeTurnDeg = (deg) => ((((deg % 360) + 540) % 360) - 180);

// Mint a client-side identifier. Delegates to the shared randomId(), which
// has a crypto.getRandomValues UUID-v4 fallback for non-secure contexts
// (LAN-IP dev/QA, where crypto.randomUUID is undefined) — the old inline
// `uid-<ts>-<rand>` fallback produced a different, non-UUID id shape and
// could exceed the 36-char identifier bound on those devices.
export const newUuid = () => randomId();

/**
 * Column-major 4x4 from an XZ-plane heading and a world translation.
 * col0 = forward (along wall), col1 = world up, col2 = col0 × col1 so the
 * basis stays right-handed (a reflected matrix would decompose with a
 * negative scale in Three.js and mirror the polygon).
 */
export const headingTransform = (headingDeg, translation) => {
    const rad = toRadians(headingDeg);
    const fx = Math.cos(rad);
    const fz = Math.sin(rad);
    const [tx, ty, tz] = translation;
    return [
        fx, 0, fz, 0,
        0, 1, 0, 0,
        -fz, 0, fx, 0,
        tx, ty, tz, 1,
    ];
};

// Identity rotation that maps the floor's local X-Y plane onto the world
// XZ ground plane (local +Y → world -Z keeps the basis right-handed).
const FLOOR_TRANSFORM = [
    1, 0, 0, 0,
    0, 0, -1, 0,
    0, 1, 0, 0,
    0, 0, 0, 1,
];

const centeredRectangle = (width, height) => ([
    [-width / 2, -height / 2, 0],
    [width / 2, -height / 2, 0],
    [width / 2, height / 2, 0],
    [-width / 2, height / 2, 0],
]);

/** Shoelace area of a polygon's first two components. Mirrors the backend. */
export const polygonArea = (corners) => {
    if (!Array.isArray(corners) || corners.length < 3) return 0;
    let acc = 0;
    for (let i = 0; i < corners.length; i++) {
        const [x0, y0] = corners[i];
        const [x1, y1] = corners[(i + 1) % corners.length];
        acc += x0 * y1 - x1 * y0;
    }
    return Math.abs(acc) / 2;
};

/**
 * Chain the parametric walk into world-space wall geometry.
 *
 * @param {Array<{uuid, lengthM, turnDeg}>} walls - each wall's length and
 *   the turn applied BEFORE it (relative to the previous wall's heading;
 *   the first wall's turnDeg is its absolute heading from +X).
 * @param {Object} opts
 * @param {string} opts.sectionUuid - parent identifier for the surfaces
 * @param {number} opts.wallHeightM
 * @param {[number, number]} [opts.start] - walk origin on the XZ plane
 * @param {number} [opts.startY] - walk elevation: every wall's BASE sits
 *   this far off the ground (ledges above half walls, raised sections).
 *   Baked into the saved transforms, so it round-trips with no schema.
 * @returns {{
 *   surfaces: Array, wallInfo: Array<{uuid, start, end, headingDeg, lengthM, heightM, baseY}>,
 *   points: Array<[number, number]>, isClosed: boolean, closureErrorM: number|null
 * }}
 */
export const buildWallSurfaces = (walls, { sectionUuid, wallHeightM = DEFAULT_WALL_HEIGHT_M, start = [0, 0], startY = 0 } = {}) => {
    const surfaces = [];
    const wallInfo = [];
    const points = [];

    let [cx, cz] = start;
    const baseY = Number.isFinite(Number(startY)) ? Number(startY) : 0;
    let headingDeg = 0;

    walls.forEach((wall, index) => {
        const length = Number(wall.lengthM);
        if (!Number.isFinite(length) || length <= 0) return;
        headingDeg += Number(wall.turnDeg) || 0;
        const height = Number(wall.heightM) > 0 ? Number(wall.heightM) : wallHeightM;

        const rad = toRadians(headingDeg);
        const sx = cx;
        const sz = cz;
        const ex = cx + Math.cos(rad) * length;
        const ez = cz + Math.sin(rad) * length;

        points.push([sx, sz]);

        surfaces.push({
            identifier: wall.uuid,
            parentIdentifier: sectionUuid,
            category: { wall: {} },
            dimensions: [length, height, 0],
            transform: headingTransform(headingDeg, [(sx + ex) / 2, baseY + height / 2, (sz + ez) / 2]),
            polygonCorners: centeredRectangle(length, height),
            walk_length_m: length,
            walk_turn_deg: Number(wall.turnDeg) || 0,
            sort_order: index,
        });
        wallInfo.push({
            uuid: wall.uuid,
            start: [sx, sz],
            end: [ex, ez],
            headingDeg,
            lengthM: length,
            heightM: height,
            baseY,
        });

        cx = ex;
        cz = ez;
    });

    let isClosed = false;
    let closureErrorM = null;
    if (points.length >= 3) {
        const [fx, fz] = points[0];
        closureErrorM = Math.hypot(cx - fx, cz - fz);
        isClosed = closureErrorM <= CLOSURE_TOLERANCE_M;
    }

    return { surfaces, wallInfo, points, isClosed, closureErrorM };
};

/**
 * Build the surface for a wall that HASN'T been added yet — the live
 * "ghost" preview of the add-wall form. Chains the candidate onto the end
 * of the section's walk so heading accumulation and the walk origin are
 * exactly what addWall would produce.
 *
 * The identifier is stable per section (not per keystroke) so the viewer's
 * React key doesn't churn while the user types. Returns null when the
 * candidate length is invalid — callers treat that as "no ghost".
 */
export const buildGhostWallSurface = (section, { lengthM, turnDeg = 0 } = {}) => {
    const length = Number(lengthM);
    if (!section || !Number.isFinite(length) || length <= 0) return null;
    const ghostWall = {
        uuid: `ghost-${section.sectionUuid}`,
        lengthM: length,
        turnDeg: Number(turnDeg) || 0,
    };
    const { surfaces } = buildWallSurfaces([...(section.walls || []), ghostWall], {
        sectionUuid: section.sectionUuid,
        wallHeightM: section.wallHeightM,
        start: section.start || [0, 0],
        startY: section.startY || 0,
    });
    const last = surfaces[surfaces.length - 1];
    return last && last.identifier === ghostWall.uuid ? last : null;
};

/**
 * Auto floor from a closed wall loop. The polygon vertices are the walk's
 * wall start points mapped into the floor's local plane (x, -z). An
 * elevated walk (startY) lifts its floor with it — a closed loop at 1m is
 * a platform, not a room whose floor fell to the ground.
 * Returns null when the loop isn't closed.
 */
export const buildFloorSurface = (points, { sectionUuid, isClosed, floorUuid, startY = 0 }) => {
    if (!isClosed || points.length < 3) return null;
    const corners = points.map(([x, z]) => [x, -z, 0]);
    const xs = corners.map((c) => c[0]);
    const ys = corners.map((c) => c[1]);
    const width = Math.max(...xs) - Math.min(...xs);
    const depth = Math.max(...ys) - Math.min(...ys);
    const transform = [...FLOOR_TRANSFORM];
    transform[13] = Number.isFinite(Number(startY)) ? Number(startY) : 0;
    return {
        identifier: floorUuid,
        parentIdentifier: sectionUuid,
        category: { floor: {} },
        dimensions: [width, depth, 0],
        transform,
        polygonCorners: corners,
        // Marks this floor as derived-from-walls so reopening the editor
        // regenerates it instead of treating it as fixed imported geometry.
        attributes: { autoGenerated: true },
        sort_order: 0,
    };
};

/**
 * Place a door/window/opening on a parametric wall: offset along the wall
 * from its start, plus sill height (0 for doors/openings) — both measured
 * from the wall's own base, so an elevated run (wall.baseY) lifts its
 * openings with it while offset_m/sill_m stay wall-relative in the save.
 * Inherits the wall's rotation; renders via the viewer's box fallback.
 */
export const buildOpeningSurface = (opening, wall, index = 0) => {
    const width = Number(opening.widthM);
    const height = Number(opening.heightM);
    if (!wall || !Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return null;
    const offset = Math.max(0, Number(opening.offsetM) || 0);
    const sill = opening.type === 'window' ? Math.max(0, Number(opening.sillM) || 0) : 0;
    const baseY = Number.isFinite(Number(wall.baseY)) ? Number(wall.baseY) : 0;

    const rad = toRadians(wall.headingDeg);
    const centerAlong = offset + width / 2;
    const cx = wall.start[0] + Math.cos(rad) * centerAlong;
    const cz = wall.start[1] + Math.sin(rad) * centerAlong;

    return {
        identifier: opening.uuid,
        parentIdentifier: wall.uuid,
        category: { [opening.type]: {} },
        dimensions: [width, height, 0],
        transform: headingTransform(wall.headingDeg, [cx, baseY + sill + height / 2, cz]),
        offset_m: offset,
        sill_m: sill,
        sort_order: index,
    };
};

/**
 * Ghost preview for the opening form — the door/window/opening that
 * HASN'T been added yet. Mirrors buildGhostWallSurface: chains the
 * section's walk to locate the target wall, then places the candidate
 * with the same math addOpening would use. Returns null when the wall
 * isn't part of this section's walk or the size is invalid — callers
 * treat that as "no ghost".
 */
export const buildGhostOpeningSurface = (
    section,
    { type, wallUuid, offsetM = 0, widthM, heightM, sillM = 0 } = {},
) => {
    if (!section || !wallUuid) return null;
    const width = Number(widthM);
    const height = Number(heightM);
    if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(height) || height <= 0) return null;
    const { wallInfo } = buildWallSurfaces(section.walls || [], {
        sectionUuid: section.sectionUuid,
        wallHeightM: section.wallHeightM,
        start: section.start || [0, 0],
        startY: section.startY || 0,
    });
    const wall = wallInfo.find((w) => w.uuid === wallUuid);
    if (!wall) return null;
    return buildOpeningSurface(
        {
            uuid: `ghost-opening-${section.sectionUuid}`,
            type,
            wallUuid,
            offsetM: Math.max(0, Number(offsetM) || 0),
            widthM: width,
            heightM: height,
            sillM,
        },
        wall,
    );
};

/**
 * Floor-plane endpoints + heading of a fixed (scan-imported) wall, read
 * from its viewer wire shape. The anchor picker uses these as candidate
 * start points for a new perimeter walk. Pure read — never mutates the
 * viewerDict (fixed surfaces must round-trip verbatim).
 *
 * Decomposition mirrors parsePlanToEditorState's start recovery: col0 of
 * the column-major 4x4 is the along-wall direction, translation lives at
 * indices 12/14, and dimensions[0] is the wall's length.
 *
 * @param {Object} viewerDict - fixedSurfaces[i].viewerDict
 * @returns {{ endA: [number, number], endB: [number, number], headingDeg: number, normalHeadingDeg: number, baseY: number } | null}
 *   null when the transform/dimensions are malformed (scan data can carry
 *   incomplete geometry — same defensive posture as the viewer).
 *   baseY is the wall's bottom-edge elevation (center y minus half its
 *   height) so anchoring to an upstairs/raised scanned wall lands the new
 *   walk at the right elevation, not on the ground.
 *   normalHeadingDeg is the wall's stored face normal (transform col2,
 *   horizontal part) — the anchor flow points the starter frame's X axis
 *   this way so X is perpendicular to the wall on the side the scanner
 *   captured (usually into the room) and Z runs along the wall. Falls back
 *   to headingDeg + 90 (our own headingTransform basis: col2 = forward ×
 *   up) when the stored column is degenerate.
 */
export const fixedWallEndpoints = (viewerDict) => {
    const t = viewerDict?.transform;
    const length = Array.isArray(viewerDict?.dimensions) ? Number(viewerDict.dimensions[0]) : NaN;
    if (!Array.isArray(t) || t.length !== 16 || !Number.isFinite(length) || length <= 0) return null;
    const fx = Number(t[0]);
    const fz = Number(t[2]);
    const cx = Number(t[12]);
    const cz = Number(t[14]);
    if (![fx, fz, cx, cz].every(Number.isFinite)) return null;
    const norm = Math.hypot(fx, fz);
    if (norm <= 0) return null;
    const ux = fx / norm;
    const uz = fz / norm;
    const half = length / 2;
    const cy = Number(t[13]);
    const heightDim = Number(viewerDict.dimensions?.[1]);
    const baseY = Number.isFinite(cy) && Number.isFinite(heightDim) ? cy - heightDim / 2 : 0;
    const headingDeg = (Math.atan2(uz, ux) * 180) / Math.PI;
    const nx = Number(t[8]);
    const nz = Number(t[10]);
    const normalHeadingDeg = Number.isFinite(nx) && Number.isFinite(nz) && Math.hypot(nx, nz) > 1e-6
        ? (Math.atan2(nz, nx) * 180) / Math.PI
        : headingDeg + 90;
    return {
        endA: [cx - ux * half, cz - uz * half],
        endB: [cx + ux * half, cz + uz * half],
        headingDeg,
        normalHeadingDeg,
        baseY,
    };
};

/**
 * Resize a fixed (scan-imported) surface: new width/height/depth with an
 * anchor rule for where the width change grows FROM. Returns a NEW
 * viewerDict — never mutates the input (untouched fixed surfaces must
 * round-trip verbatim) — or null on malformed input, so callers can treat
 * null as "no-op" instead of corrupting state.
 *
 * Spread-preserves every other field (identifier, parentIdentifier,
 * attributes, sort_order, walk_*), keeping uniqueness and parent links
 * intact across a resize.
 *
 * Anchor semantics (walls; doors/windows/openings resize about center):
 *  - 'center' (default): the center point stays — Revit's typed
 *    temporary-dimension behavior.
 *  - 'endA' / 'endB': THAT end's world position stays pinned and the
 *    opposite end moves — Revit's endpoint-grip stretch. Direction comes
 *    from transform col0 (t[0]/t[2]), the same basis fixedWallEndpoints
 *    reads; a zero-norm column refuses (null) rather than guessing.
 *
 * Height is ALWAYS bottom-anchored: the bottom edge (center.y − h/2)
 * stays, only the top moves — a wall's base stays on the floor and a
 * window's sill stays put. Inherits fixedWallEndpoints' assumption that
 * these surfaces are gravity-aligned (transform col1 = world up).
 *
 * depthM: undefined (key absent from the patch) preserves the stored
 * depth — a width/height-only edit must not clear it. An explicit
 * null/0/non-positive value resets to 0 ("assumed standard" — the viewer
 * renders its default thickness), so clearing the Depth field genuinely
 * un-sets a previously stored value. A finite positive value persists.
 */
export const resizeFixedSurface = (viewerDict, { widthM, heightM, depthM, anchor = 'center' } = {}) => {
    const t = viewerDict?.transform;
    const dims = viewerDict?.dimensions;
    if (!Array.isArray(t) || t.length !== 16 || !Array.isArray(dims) || dims.length < 2) return null;

    const oldWidth = Number(dims[0]);
    const oldHeight = Number(dims[1]);
    if (![oldWidth, oldHeight].every((n) => Number.isFinite(n) && n > 0)) return null;

    const newWidth = Number.isFinite(Number(widthM)) && Number(widthM) > 0 ? Number(widthM) : oldWidth;
    const newHeight = Number.isFinite(Number(heightM)) && Number(heightM) > 0 ? Number(heightM) : oldHeight;
    const oldDepth = Number.isFinite(Number(dims[2])) && Number(dims[2]) > 0 ? Number(dims[2]) : 0;
    const newDepth = depthM === undefined
        ? oldDepth
        : (Number.isFinite(Number(depthM)) && Number(depthM) > 0 ? Number(depthM) : 0);

    const cx = Number(t[12]);
    const cyOld = Number(t[13]);
    const cz = Number(t[14]);
    if (![cx, cyOld, cz].every(Number.isFinite)) return null;

    const newCy = (cyOld - oldHeight / 2) + newHeight / 2;

    let newCx = cx;
    let newCz = cz;
    if (anchor === 'endA' || anchor === 'endB') {
        const fx = Number(t[0]);
        const fz = Number(t[2]);
        const norm = Math.hypot(fx, fz);
        if (norm <= 0) return null;
        const ux = fx / norm;
        const uz = fz / norm;
        // endA = center − half·u, endB = center + half·u. Holding an end
        // fixed shifts the center by ±(halfNew − halfOld) along u.
        const shift = (newWidth / 2) - (oldWidth / 2);
        const sign = anchor === 'endA' ? 1 : -1;
        newCx = cx + sign * shift * ux;
        newCz = cz + sign * shift * uz;
    }

    const newTransform = [...t];
    newTransform[12] = newCx;
    newTransform[13] = newCy;
    newTransform[14] = newCz;

    // Non-empty explicit outlines (sloped/gable scan walls) scale
    // proportionally so their shape survives the resize; the common
    // rectangular case ships polygonCorners: [] and re-renders via
    // inferRectanglePolygon from the new dimensions.
    let newPolygonCorners = viewerDict.polygonCorners;
    if (Array.isArray(newPolygonCorners) && newPolygonCorners.length >= 3) {
        const scaleX = newWidth / oldWidth;
        const scaleY = newHeight / oldHeight;
        newPolygonCorners = newPolygonCorners.map((corner) => (
            Array.isArray(corner)
                ? [corner[0] * scaleX, corner[1] * scaleY, ...corner.slice(2)]
                : corner
        ));
    }

    return {
        ...viewerDict,
        dimensions: [newWidth, newHeight, newDepth],
        transform: newTransform,
        polygonCorners: newPolygonCorners,
    };
};

/**
 * A fixed window's sill: its bottom edge's height above its parent wall's
 * base. Scan surfaces never carry sill_m (only parametric openings do),
 * so the editor derives it from the two transforms instead. Null when
 * either surface is malformed.
 */
export const fixedWindowSill = (windowViewerDict, wallViewerDict) => {
    const wt = windowViewerDict?.transform;
    const windowHeight = Array.isArray(windowViewerDict?.dimensions)
        ? Number(windowViewerDict.dimensions[1])
        : NaN;
    if (!Array.isArray(wt) || wt.length !== 16 || !Number.isFinite(windowHeight) || windowHeight <= 0) return null;
    const windowCy = Number(wt[13]);
    if (!Number.isFinite(windowCy)) return null;
    const wallEnds = fixedWallEndpoints(wallViewerDict);
    if (!wallEnds) return null;
    return (windowCy - windowHeight / 2) - wallEnds.baseY;
};

/**
 * Move a fixed window vertically so its bottom sits sillM above its
 * parent wall's base. Translation-only counterpart to resizeFixedSurface
 * — dimensions are untouched. Separate function (not a resize param)
 * because it needs the PARENT wall's baseY, which the window's own
 * viewerDict can't provide. Returns a new viewerDict or null.
 */
export const resizeFixedWindowSill = (windowViewerDict, wallViewerDict, sillM) => {
    const sill = Number(sillM);
    if (!Number.isFinite(sill) || sill < 0) return null;
    const wt = windowViewerDict?.transform;
    const windowHeight = Array.isArray(windowViewerDict?.dimensions)
        ? Number(windowViewerDict.dimensions[1])
        : NaN;
    if (!Array.isArray(wt) || wt.length !== 16 || !Number.isFinite(windowHeight) || windowHeight <= 0) return null;
    const wallEnds = fixedWallEndpoints(wallViewerDict);
    if (!wallEnds) return null;
    const newTransform = [...wt];
    newTransform[13] = wallEnds.baseY + sill + windowHeight / 2;
    return { ...windowViewerDict, transform: newTransform };
};

/**
 * Ghost pillar marking the PINNED end of a fixed wall while its width is
 * being edited with an end anchor. Taller and thinner than the anchor-
 * corner stubs (which can occupy the same corners simultaneously — the
 * anchor card shows for the same selected wall) and in its own accent
 * color, so "the lime pillar stays put" reads unambiguously. Ghost-only —
 * never persisted — and null for the center anchor, which needs no pin.
 */
export const buildResizePinMarker = (endpoints, { sectionUuid, pinnedEnd } = {}) => {
    if (pinnedEnd !== 'endA' && pinnedEnd !== 'endB') return null;
    const point = endpoints?.[pinnedEnd];
    if (!Array.isArray(point) || !point.every(Number.isFinite)) return null;
    const baseY = Number.isFinite(Number(endpoints.baseY)) ? Number(endpoints.baseY) : 0;
    return {
        identifier: `resize-${pinnedEnd}-${sectionUuid}`,
        parentIdentifier: sectionUuid,
        category: { object: {} },
        dimensions: [0.06, 0.6, 0.06],
        transform: headingTransform(0, [point[0], baseY + 0.3, point[1]]),
        polygonCorners: [],
        colorOverride: RESIZE_PIN_COLOR,
        opacity: 0.9,
        attributes: {},
    };
};

/**
 * Children of a fixed wall (doors/windows/openings whose parentIdentifier
 * matches). Single source of truth for BOTH the delete-confirmation count
 * and the actual cascade removal, so the number shown in the modal can
 * never drift from what gets deleted.
 */
export const cascadeDeleteFixedSurface = (fixedSurfaces, wallIdentifier) => (
    (fixedSurfaces || []).filter((f) => f.viewerDict?.parentIdentifier === wallIdentifier)
);

/**
 * Flag a fixed wall's children that extend past its (possibly resized)
 * extent. Children carry their own world transforms and do NOT move when
 * the wall resizes, so shrinking can strand a door/window outside the new
 * outline. Non-blocking — mirrors the parametric openingFitWarnings
 * contract (warn, don't clamp).
 *
 * Same local-frame projection as the viewer's computeWallHoles, but on
 * plain arrays: this module stays Three.js-free by design.
 *
 * @returns {Array<{identifier, axis: 'width'|'height', overflowM}>} one
 *   entry per overflowing axis; empty on malformed input, never throws.
 */
export const fixedSurfaceFitWarning = (wallViewerDict, childViewerDicts) => {
    const wt = wallViewerDict?.transform;
    const wdims = wallViewerDict?.dimensions;
    if (!Array.isArray(wt) || wt.length !== 16 || !Array.isArray(wdims)) return [];
    const wallWidth = Number(wdims[0]);
    const wallHeight = Number(wdims[1]);
    if (![wallWidth, wallHeight].every((n) => Number.isFinite(n) && n > 0)) return [];

    const fx = Number(wt[0]);
    const fz = Number(wt[2]);
    const norm = Math.hypot(fx, fz);
    if (!Number.isFinite(norm) || norm <= 0) return [];
    const ux = fx / norm;
    const uz = fz / norm;
    const cx = Number(wt[12]);
    const cy = Number(wt[13]);
    const cz = Number(wt[14]);
    if (![cx, cy, cz].every(Number.isFinite)) return [];

    const warnings = [];
    (childViewerDicts || []).forEach((child) => {
        const ct = child?.transform;
        const cdims = child?.dimensions;
        if (!Array.isArray(ct) || ct.length !== 16 || !Array.isArray(cdims)) return;
        const childW = Number(cdims[0]);
        const childH = Number(cdims[1]);
        if (![childW, childH].every((n) => Number.isFinite(n) && n > 0)) return;
        const dx = Number(ct[12]) - cx;
        const dy = Number(ct[13]) - cy;
        const dz = Number(ct[14]) - cz;
        if (![dx, dy, dz].every(Number.isFinite)) return;
        // Walls never tilt (col1 = world up), so the world Y delta IS the
        // local vertical delta and the XZ dot with u is the along-wall one.
        const alongOverflow = (Math.abs(dx * ux + dz * uz) + childW / 2) - wallWidth / 2;
        const upOverflow = (Math.abs(dy) + childH / 2) - wallHeight / 2;
        if (alongOverflow > 1e-6) {
            warnings.push({ identifier: child.identifier, axis: 'width', overflowM: alongOverflow });
        }
        if (upOverflow > 1e-6) {
            warnings.push({ identifier: child.identifier, axis: 'height', overflowM: upOverflow });
        }
    });
    return warnings;
};

/**
 * Absolute heading (degrees from +X, NOT normalized — callers pass it
 * through normalizeTurnDeg) for a walk anchored to a fixed wall.
 *
 * 'continue' extends the wall's own line outward from the chosen end
 * (endB → along the wall's forward direction; endA → the opposite), so a
 * new wall never walks back over the scanned one. 'reverse' is the
 * opposite colinear direction (back along/over the wall). 'left'/'right'
 * are perpendicular to the wall, independent of the chosen end.
 *
 * @param {number} wallHeadingDeg - fixedWallEndpoints(...).headingDeg
 * @param {'endA'|'endB'} end
 * @param {'continue'|'reverse'|'left'|'right'} preset
 */
export const headingForAnchorPreset = (wallHeadingDeg, end, preset) => {
    const outward = end === 'endA' ? wallHeadingDeg + 180 : wallHeadingDeg;
    switch (preset) {
        case 'reverse':
            return outward + 180;
        case 'left':
            return wallHeadingDeg - 90;
        case 'right':
            return wallHeadingDeg + 90;
        case 'continue':
        default:
            return outward;
    }
};

/**
 * Small non-interactive marker at the section's walk origin, rendered via
 * the viewer's ghost list so the user can see where a fresh walk starts
 * (especially before any wall exists, when nothing else renders there) and
 * watch nudges land in real time. Empty polygonCorners deliberately routes
 * it through the viewer's box-fallback path. Ghosts never reach
 * buildSavePayload, so its `origin-` identifier never has to fit a
 * persisted-column bound.
 *
 * @param {Object} section
 * @param {Object} [opts]
 * @param {number} [opts.headingDeg] - the starter FRAME rotation; bakes
 *   into the marker's transform so the viewer's gizmo axes (rendered in
 *   the marker's local frame) spin live as the rotation field changes.
 * @param {boolean} [opts.gizmo] - mount the frame gizmo here. Turned off
 *   while an anchor preview is active so the chosen corner's preview is
 *   the single gizmo on screen.
 */
export const buildOriginMarker = (section, { headingDeg = 0, gizmo = true } = {}) => {
    const start = section?.start;
    if (!Array.isArray(start) || !Number.isFinite(start[0]) || !Number.isFinite(start[1])) return null;
    const [x, z] = start;
    const baseY = Number.isFinite(Number(section.startY)) ? Number(section.startY) : 0;
    return {
        identifier: `origin-${section.sectionUuid}`,
        parentIdentifier: section.sectionUuid,
        category: { object: {} },
        dimensions: [0.15, 0.3, 0.15],
        transform: headingTransform(Number(headingDeg) || 0, [x, baseY + 0.15, z]),
        polygonCorners: [],
        // walkOrigin tells the viewer to mount the labeled world-axis gizmo
        // here, so the colored lines the nudge arrows move along are always
        // visible while editing (ghost-only — never persisted). The separate
        // heading arrow this once also mounted was removed in cd4eed5 when the
        // starter frame became rotatable — the axes carry that signal now.
        attributes: { walkOrigin: !!gizmo },
    };
};

/**
 * Preview markers for the two candidate anchor corners of a selected
 * scanned wall. The chosen end is the full start-point preview — heading
 * accent color plus the frame gizmo (walkOrigin flag) rotated to the
 * wall-aligned frame "Use as start point" would apply (X ⟂ wall on its
 * scanned-normal side, Z along it) — while the other end renders as a
 * subdued candidate the user can still recognize and switch to.
 * `anchorCorner` also tells the viewer to suppress the selected wall's
 * own center gizmo: while anchoring, the corners are the reference points
 * that matter. Ghost-only — never persisted.
 */
export const buildAnchorCornerMarkers = (endpoints, { sectionUuid, chosenEnd = 'endA', headingDeg = 0 } = {}) => {
    if (!endpoints?.endA || !endpoints?.endB) return [];
    const baseY = Number.isFinite(Number(endpoints.baseY)) ? Number(endpoints.baseY) : 0;
    return ['endA', 'endB'].map((endKey) => {
        const [x, z] = endpoints[endKey];
        const chosen = endKey === chosenEnd;
        const height = chosen ? 0.3 : 0.18;
        return {
            identifier: `anchor-${endKey}-${sectionUuid}`,
            parentIdentifier: sectionUuid,
            category: { object: {} },
            dimensions: chosen ? [0.15, height, 0.15] : [0.1, height, 0.1],
            transform: headingTransform(chosen ? (Number(headingDeg) || 0) : 0, [x, baseY + height / 2, z]),
            polygonCorners: [],
            // Scene accent colors (see sceneUtils) — the viewer honors
            // per-ghost overrides and falls back to ghost blue otherwise.
            ...(chosen ? { colorOverride: HEADING_COLOR, opacity: 0.85 } : { opacity: 0.4 }),
            attributes: chosen ? { walkOrigin: true, anchorCorner: true } : { anchorCorner: true },
        };
    });
};

const EMPTY_DATA = () => ({ walls: [], doors: [], windows: [], openings: [], floors: [], objects: [] });

const OPENING_KEY = { door: 'doors', window: 'windows', opening: 'openings' };

const SURFACE_TYPE_KEY = { wall: 'walls', door: 'doors', window: 'windows', opening: 'openings', floor: 'floors' };

/**
 * Derive the full surface set for one editor section: chained walls, their
 * openings, the auto floor (when the loop closes), plus any fixed surfaces
 * (scan-converted geometry carried through verbatim).
 */
export const deriveSectionSurfaces = (section) => {
    const data = EMPTY_DATA();
    const { surfaces, wallInfo, points, isClosed, closureErrorM } = buildWallSurfaces(section.walls || [], {
        sectionUuid: section.sectionUuid,
        wallHeightM: section.wallHeightM,
        start: section.start || [0, 0],
        startY: section.startY || 0,
    });
    data.walls.push(...surfaces);

    const wallByUuid = new Map(wallInfo.map((w) => [w.uuid, w]));
    (section.openings || []).forEach((opening, index) => {
        const surface = buildOpeningSurface(opening, wallByUuid.get(opening.wallUuid), index);
        if (surface) data[OPENING_KEY[opening.type]].push(surface);
    });

    const floor = buildFloorSurface(points, {
        sectionUuid: section.sectionUuid,
        isClosed,
        floorUuid: section.floorUuid || `floor-${section.sectionUuid}`,
        startY: section.startY || 0,
    });
    if (floor) data.floors.push(floor);

    (section.fixedSurfaces || []).forEach((surface) => {
        const key = SURFACE_TYPE_KEY[surface.surface_type];
        if (key) data[key].push(surface.viewerDict);
    });

    const floorArea = data.floors.reduce((sum, f) => sum + polygonArea(f.polygonCorners || []), 0);

    return { data, isClosed, closureErrorM, floorArea: floorArea > 0 ? floorArea : null, wallInfo };
};

/**
 * Build the previewData blob FloorPlanViewer consumes — identical in shape
 * to the GET /user-floor-plans/:id response, so the live editor preview
 * and the saved plan render through the same path. The floor area here is
 * a client-side preview; the backend recomputes the authoritative value.
 *
 * `ghosts` (surfaces from buildGhostWallSurface / buildGhostOpeningSurface)
 * ride along in a separate `data.ghosts` list — deliberately NOT in
 * EMPTY_DATA/SURFACE_TYPE_KEY, so they can never reach buildSavePayload,
 * the floor area, or the gap math. Null entries are dropped so callers can
 * pass `[wallGhost, openingGhost]` without filtering.
 */
export const buildViewerPayload = (sections, { ghosts = [] } = {}) => {
    const data = EMPTY_DATA();
    const floorSections = [];
    let totalArea = 0;

    sections.forEach((section) => {
        const derived = deriveSectionSurfaces(section);
        Object.keys(data).forEach((key) => data[key].push(...derived.data[key]));
        if (derived.floorArea) {
            totalArea += derived.floorArea;
            floorSections.push({
                section_uuid: section.sectionUuid,
                label: section.label,
                floor_area: derived.floorArea,
                floor_area_source: 'polygon_computed',
                floor_area_unit: 'm2',
                floor_footprint_m: null,
            });
        }
    });

    return {
        data: { ...data, ghosts: ghosts.filter(Boolean) },
        floor_area: totalArea > 0 ? totalArea : null,
        floor_area_source: totalArea > 0 ? 'polygon_computed' : null,
        floor_area_unit: totalArea > 0 ? 'm2' : null,
        floor_sections: floorSections,
        floor_footprint_m: null,
    };
};

const surfaceToSavePayload = (surface, surfaceType) => ({
    surface_uuid: surface.identifier,
    parent_uuid: surface.parentIdentifier ?? null,
    surface_type: surfaceType,
    dimensions: surface.dimensions,
    transform: surface.transform,
    polygon_corners: surface.polygonCorners || [],
    attributes: surface.attributes ?? null,
    walk_length_m: surface.walk_length_m ?? null,
    walk_turn_deg: surface.walk_turn_deg ?? null,
    offset_m: surface.offset_m ?? null,
    sill_m: surface.sill_m ?? null,
    sort_order: surface.sort_order ?? 0,
});

/**
 * Build the PUT /user-floor-plans/:id/surfaces body from editor sections.
 * Pass `name` to rename the plan in the SAME atomic request — splitting it
 * into a separate PATCH would let a geometry failure leave a half-applied
 * rename.
 */
export const buildSavePayload = (sections, { name } = {}) => ({
    ...(name != null ? { name } : {}),
    sections: sections.map((section, index) => {
        const derived = deriveSectionSurfaces(section);
        const surfaces = [];
        Object.entries(SURFACE_TYPE_KEY).forEach(([surfaceType, dataKey]) => {
            derived.data[dataKey].forEach((surface) => {
                surfaces.push(surfaceToSavePayload(surface, surfaceType));
            });
        });
        return {
            section_uuid: section.sectionUuid,
            label: section.label || null,
            wall_height_m: Number(section.wallHeightM) > 0 ? Number(section.wallHeightM) : DEFAULT_WALL_HEIGHT_M,
            sort_order: index,
            surfaces,
        };
    }),
});

/**
 * Reconstruct editor state from a saved plan response.
 *
 * Surfaces WITH parametric walk inputs become editable walls/openings;
 * everything else (scan-converted geometry, whose walk was never recorded)
 * becomes a fixed surface that re-saves verbatim. Auto-generated floors
 * are dropped here and regenerated from the walls on the next derive.
 */
export const parsePlanToEditorState = (plan) => {
    const data = plan?.data || {};
    const sectionsMeta = plan?.sections || [];

    const sections = sectionsMeta.map((meta) => ({
        sectionUuid: meta.section_uuid,
        label: meta.label || '',
        wallHeightM: meta.wall_height_m || DEFAULT_WALL_HEIGHT_M,
        start: [0, 0],
        startY: 0,
        // Editor-session orientation of the starter frame (gizmo axes,
        // nudge directions, first-wall heading reference). Deliberately
        // never persisted — walls store absolute headings, so saved plans
        // are frame-independent and this resets to 0 on reopen.
        frameRotationDeg: 0,
        walls: [],
        openings: [],
        fixedSurfaces: [],
        floorUuid: null,
    }));
    const bySectionUuid = new Map(sections.map((s) => [s.sectionUuid, s]));
    const fallbackSection = sections[0];

    const sectionFor = (parentUuid) => bySectionUuid.get(parentUuid) || fallbackSection;

    const parametricWallUuids = new Set();
    const wallSectionByUuid = new Map();

    (data.walls || []).forEach((wall) => {
        const section = sectionFor(wall.parentIdentifier);
        if (!section) return;
        wallSectionByUuid.set(wall.identifier, section);
        if (wall.walk_length_m != null) {
            parametricWallUuids.add(wall.identifier);
            // Per-wall height overrides live only in dimensions[1]; a value
            // matching the section height (within epsilon) parses as "no
            // override" so the wall keeps following section-height edits.
            const dimHeight = Array.isArray(wall.dimensions) ? Number(wall.dimensions[1]) : NaN;
            const hasHeightOverride = Number.isFinite(dimHeight)
                && Math.abs(dimHeight - section.wallHeightM) > HEIGHT_OVERRIDE_EPSILON_M;
            section.walls.push({
                uuid: wall.identifier,
                lengthM: wall.walk_length_m,
                turnDeg: wall.walk_turn_deg ?? 0,
                sortOrder: wall.sort_order ?? 0,
                ...(hasHeightOverride ? { heightM: dimHeight } : {}),
            });
            // Recover the section's walk origin from the first wall's
            // transform (translation minus half the wall along its heading).
            // Elevation comes from the same transform: the wall centers at
            // startY + height/2, so its base is t[13] − dims[1]/2. Values
            // within display-rounding noise of the ground snap to 0 so an
            // unelevated run doesn't grow a phantom 0.0001m offset.
            if (section.walls.length === 1 && Array.isArray(wall.transform) && wall.transform.length === 16) {
                const t = wall.transform;
                const half = wall.walk_length_m / 2;
                section.start = [t[12] - t[0] * half, t[14] - t[2] * half];
                const wallHeight = Number.isFinite(dimHeight) ? dimHeight : section.wallHeightM;
                const recoveredY = Number(t[13]) - wallHeight / 2;
                section.startY = Number.isFinite(recoveredY) && Math.abs(recoveredY) > HEIGHT_OVERRIDE_EPSILON_M
                    ? recoveredY
                    : 0;
            }
        } else {
            section.fixedSurfaces.push({ surface_type: 'wall', viewerDict: wall });
        }
    });
    sections.forEach((s) => s.walls.sort((a, b) => a.sortOrder - b.sortOrder));

    ['doors', 'windows', 'openings'].forEach((key) => {
        const type = { doors: 'door', windows: 'window', openings: 'opening' }[key];
        (data[key] || []).forEach((surface) => {
            const parentIsParametricWall = parametricWallUuids.has(surface.parentIdentifier);
            if (parentIsParametricWall && surface.offset_m != null) {
                const section = wallSectionByUuid.get(surface.parentIdentifier);
                section.openings.push({
                    uuid: surface.identifier,
                    type,
                    wallUuid: surface.parentIdentifier,
                    offsetM: surface.offset_m,
                    widthM: surface.dimensions?.[0],
                    heightM: surface.dimensions?.[1],
                    sillM: surface.sill_m ?? 0,
                });
            } else {
                const section = wallSectionByUuid.get(surface.parentIdentifier) || sectionFor(surface.parentIdentifier);
                if (section) section.fixedSurfaces.push({ surface_type: type, viewerDict: surface });
            }
        });
    });

    (data.floors || []).forEach((floor) => {
        const section = sectionFor(floor.parentIdentifier);
        if (!section) return;
        if (floor.attributes?.autoGenerated) {
            // Regenerated from the wall loop on derive; keep the UUID stable
            // so viewer selection/visibility survives an edit session.
            section.floorUuid = floor.identifier;
        } else {
            section.fixedSurfaces.push({ surface_type: 'floor', viewerDict: floor });
        }
    });

    return sections;
};
